import * as React from 'react';
import * as _ from 'lodash';
import { Controlled as ReactCodeMirror } from 'react-codemirror2';
import { CodeMirrorType } from '../../../../../models/CodeMirrorType';
import ComboBox from '../../../../components/ComboBox/ComboBox';
import TextFieldWithJsonWrapper from '../../../../components/TextFieldWithJsonWrapper/TextFieldWithJsonWrapper';
import { defaultTransformColumnFormat, getTransformFormatsByCellType, transformFormatsEditorModes } from '../transformFormats';
import { TransformHtmlViewer } from '../TransformHtmlViewer';
import { TransformMarkdownViewer } from '../TransformMarkdownViewer';
import { AutoFormatIconSvg, CodeIconSvg, EyeIconSvg, SplitIconSvg } from '../../AlanButtonPopupSettingsForm/alanPopupEditorIcons';
import { ITransformRow, TransformFormatType } from '../../../../../models/ITransform';
import { FillTransformResultButton } from '../FillTransformResultButton/FillTransformResultButton';
import { Loader, LoaderSize, LoaderTheme } from '../../../../components/Loader/Loader';
import { convertTransformPropValueToString, formatValueForTransform } from '../formatValueForTransform';
import { getValidationMsgForJSONFromTransform, isTransformValueValidJSON, formatTagsAndJSONString } from '../cleanupTransformsJSON';
import ToasterService from '../../../../../services/ToasterService';
import cancelEvent from '../../../../../services/mini/cancelEvent';
import { getMarkdownWithTransformFixes } from '../../../../../services/mini/getMarkdownWithTransformFixes';
import { CountTransformTokens } from '../CountTransformTokens/CountTransformTokens';
import transformsSettings from '../../../../../const/transformsSettings';

import { HTML_CM_TRANSFORM_MODE, JS_CM_TRANSFORM_MODE, TEXT_CM_TRANSFORM_MODE } from '../cm_transform_modes';
import '../../../../../services/cm/transformsKeywords';
import copyJSONToBuffer from '../../../../../services/mini/copyJSONToBuffer';
import RunScriptButton from '../RunScriptButton/RunScriptButton';
import { Environment } from '../../../../../const/environment';
import { observer } from 'mobx-react';
import getCodeMirrorInstance from '../../../../../services/mini/getCodeMirrorInstance';
import { transformsStore } from '../../../../../mobx/transforms.store';
import { TransformFormatListItem } from '../TransformFormatListItem/TransformFormatListItem';
import OpenJsFiddleButton from '../OpenJsFiddleButton/OpenJsFiddleButton';


const htmlPretty = require('pretty');

const options = {
    tabSize: transformsSettings.tabSize,
    indentUnit: transformsSettings.indentUnit,
    lineWrapping: false,
    mode: TEXT_CM_TRANSFORM_MODE,
    showCursorWhenSelecting: true,
};

const cbWidth = {
    'text': '46px',
    'json': '46px',
    'html': '46px',
    'javascript': '82px',
    'markdown': '82px',
};


type TransformRowPopupColumnProps = {
    initValue: string
    title: string
    name: string
    colMode: string
    format: TransformFormatType
    editable: boolean
    hideFormatSelector?: boolean
    onUpdate: (value: string, name: string) => void;
    collapsed: boolean;
    canBeCollapsed: boolean
    onToggleColumn: (collapsed: boolean, name: string) => void;
    onSplitModeClick: (name: string, modeName: string) => void;
    onFormatChange: (name: string, value: TransformFormatType) => void;
    transformName: string;
    row: ITransformRow
    environment: Environment
    index: number
}


type TransformRowPopupColumnState = {
    viewer: string
    curValue: string
    selectedFormat: TransformFormatType
    isListOpened: boolean
}

class TransformRowPopupColumn extends React.Component<TransformRowPopupColumnProps, TransformRowPopupColumnState> {
    ref;
    constructor(props) {
        super(props);

        this.state = {
            curValue: this.setValue(this.props.initValue),
            viewer: this.props.colMode,
            selectedFormat: this.props.format || defaultTransformColumnFormat,
            isListOpened: false
        };

        this.ref = React.createRef<CodeMirrorType>();
    }

    componentDidMount(): void {
        this.addHotKeys()
    }

    componentDidUpdate(prevProps): void {
        if (prevProps.initValue !== this.props.initValue) {
            this.setState({ curValue: this.setValue(this.props.initValue) });
        }
        if (prevProps.colMode !== this.props.colMode) {
            this.setState({ viewer: this.props.colMode });
        }
    }

    addHotKeys = () => {
        const editor = getCodeMirrorInstance(this.ref.current);
        if (!editor) return;
        editor.setOption('extraKeys', {
            'Cmd-Z': this.undo,
            'Ctrl-Z': this.undo,
        });
    }

    undo = () => {
        const { row } = this.props;
        const editor = getCodeMirrorInstance(this.ref.current);

        transformsStore.revertResultBeforeGeneration(row.id);
        if (editor) {
            editor.undo();
        }
    }

    setValue = (val) => {
        return convertTransformPropValueToString(val);
    }

    minifyValue = () => {
        const { selectedFormat, curValue, } = this.state;
        const { onUpdate, name, } = this.props;
        try {
            if (selectedFormat === 'json') {
                const newVal = formatTagsAndJSONString(curValue, true);
                this.setState({ curValue: newVal });
                onUpdate(newVal, name);
            }
        } catch (error) {
        }
    }

    formatValue = () => {
        const { selectedFormat, curValue, } = this.state;
        const { onUpdate, name, } = this.props;
        try {
            if (selectedFormat === 'json') {
                const newVal = formatTagsAndJSONString(curValue, false);
                this.setState({ curValue: newVal });
                onUpdate(newVal, name);
            }
            if (selectedFormat === 'html') {
                const newVal = htmlPretty(curValue);
                this.setState({ curValue: newVal });
                onUpdate(newVal, name);
            }

        } catch (error) {
        }
    }

    handleEditMode = () => {
        this.setState({ viewer: 'editor' });
        this.props.onSplitModeClick(this.props.name, 'editor');
    }
    handleSplittedMode = () => {
        this.setState({ viewer: 'both' });
        this.props.onSplitModeClick(this.props.name, 'both');
    }
    handleViewMode = () => {
        this.setState({ viewer: 'preview' });
        this.props.onSplitModeClick(this.props.name, 'preview');
    }

    handleGenerationFinish = () => {
        setTimeout(() => {
            const editor = getCodeMirrorInstance(this.ref.current);
            if (editor) {
                editor.focus();
            }
        }, 1200);
    }

    render() {
        const { transformName, row, environment, index } = this.props;
        const { viewer, selectedFormat, curValue, isListOpened } = this.state;
        const { title, hideFormatSelector, editable, onUpdate, name, onToggleColumn, canBeCollapsed, collapsed, onFormatChange } = this.props;
        const isValid = isTransformValueValidJSON(selectedFormat, curValue);
        const jsonValidationMsg = getValidationMsgForJSONFromTransform(selectedFormat, curValue);
        const canBeWrapped = false; //viewer === 'editor' ? (curValue || '')?.split(/\n/).length < 19 : false;
        const inputKey = `${collapsed}`;
        const isLoading = row.isResultLoading && name === 'result';

        return (
            <div className={`transform-row-popup-column ${collapsed && 'collapsed'}`}>
                <div className="transform-row-popup-column__mini-panel" onClick={() => {
                    onToggleColumn(!collapsed, name);
                }}>
                    <i
                        title={!collapsed ? `Collapse ${title} panel` : `Expand ${title} panel`}
                        className={`icon ${!collapsed ? 'icon-alan-chevron-fat' : 'icon-alan-chevron-fat collapsed'} transform-row-popup-column__expander in-mini-panel ${(!canBeCollapsed && !collapsed) ? 'disabled' : ''}`}
                    />
                    <div className="transform-row-popup-column__header-label">{title}</div>
                </div>

                <div className="transform-row-popup-column__header">
                    <i
                        onClick={() => {
                            onToggleColumn(!collapsed, name);
                        }}
                        title={!collapsed ? `Collapse ${title} panel` : `Expand ${title} panel`}
                        className={`icon ${!collapsed ? 'icon-alan-chevron-fat' : 'icon-alan-chevron-fat collapsed'} transform-row-popup-column__expander in-header ${(!canBeCollapsed && !collapsed) ? 'disabled' : ''}`}
                    />
                    <div className={`transform-row-popup-column__header-label ${!hideFormatSelector && 'with-format-selector'}`}>{title}</div>

                    <div className="transform-row-popup-column__header-ctrls">


                        {(editable && !collapsed && name === 'result') &&
                            <FillTransformResultButton
                                row={row}
                                index={index}
                                isVisibleAlways={true}
                                left={0}
                                top={0}
                                environment={environment}
                                className="transform-row-popup-column__run-btn"
                                onFinish={this.handleGenerationFinish}
                            />}

                        {(!collapsed && name === 'result') &&
                            <OpenJsFiddleButton
                                value={curValue}
                                width={20}
                                height={26}
                                className="transform-row-popup-column__open-fiddle-btn"
                            />
                        }

                        {(!collapsed && name === 'result' && selectedFormat === 'javascript') && <RunScriptButton
                            text={row[name] || ''}
                            className="transform-row-popup-column__run-script-btn"
                            transformName={transformName}
                            environment={environment} />}


                        {(selectedFormat === 'json') &&
                            <div className={`transform-row-popup-column__header-reformat-btn`}
                                title={`Minify ${selectedFormat.toUpperCase()}`}
                                onClick={this.minifyValue}>
                                {`{...}`}
                            </div>

                        }

                        {(selectedFormat === 'html' || selectedFormat === 'json') &&
                            <div className={`transform-row-popup-column__header-reformat-btn`}
                                title={`Format ${selectedFormat.toUpperCase()}`}
                                onClick={this.formatValue}>
                                <AutoFormatIconSvg />
                            </div>
                        }

                        {(shouldShowEditorModeSelector(selectedFormat)) && <div className={`transform-row-popup-column__header-modes`}>
                            <div
                                title="Code"
                                className={`transform-row-popup-column__header-mode-btn ${viewer === 'editor' ? 'selected' : ''}`}
                                onClick={this.handleEditMode}>
                                <CodeIconSvg />
                            </div>
                            <div
                                title="Split"
                                className={`transform-row-popup-column__header-mode-btn ${viewer === 'both' ? 'selected' : ''}`}
                                onClick={this.handleSplittedMode}>
                                <SplitIconSvg />
                            </div>
                            <div
                                title="Preview"
                                className={`transform-row-popup-column__header-mode-btn ${viewer === 'preview' ? 'selected' : ''}`}
                                onClick={this.handleViewMode}>
                                <EyeIconSvg />
                            </div>
                        </div>}

                        {hideFormatSelector ? <div></div> :

                            <ComboBox
                                classes={`transform-row-popup-column__cb with-${selectedFormat}`}
                                values={getTransformFormatsByCellType(selectedFormat, name)}
                                valueKey="value"
                                valueName="label"
                                minWidth="26px"
                                width="60%"
                                maxWidth={cbWidth[selectedFormat] || '90px'}
                                disabled={!editable}
                                listItemCount={5}
                                selectedValueId={selectedFormat}
                                onOpen={() => { this.setState({ isListOpened: true }); }}
                                onClose={() => { this.setState({ isListOpened: false }); }}
                                renderItem={(item) => <TransformFormatListItem
                                    item={item}
                                    className='transform-row-popup-column__cb-item'
                                />}
                                onValueSelect={(formatItem) => {
                                    this.setState({ selectedFormat: formatItem.value });
                                    if (editable) {
                                        onFormatChange(name, formatItem.value);
                                    }
                                }}
                            />}

                    </div>



                </div>


                <div className={`transform-row-popup-column__body ${isListOpened ? 'list-opened' : ''}`}>
                    {(editable && !collapsed && name === 'result') &&
                        <FillTransformResultButton
                            row={row}
                            index={index}
                            isVisibleAlways={true}
                            left={0}
                            top={0}
                            environment={environment}
                            onFinish={this.handleGenerationFinish}
                        />}

                    <CountTransformTokens value={curValue} />




                    {(!collapsed) && <div
                        className={`transform-row-popup-column__copy-btn ${(isLoading || !curValue) && 'disabled'}`}
                        title="Copy value"
                        onClick={(e) => {
                            copyJSONToBuffer(curValue);
                            ToasterService.showInfo({ title: `Copied!` });
                            cancelEvent(e);
                        }}>
                        <i className="icon icon-alan-test-copy" />
                    </div>}

                    {selectedFormat === 'text' &&
                        <>
                            <div className="transform-row-popup-column__one-col-editor  ">
                                <ReactCodeMirror
                                    key={inputKey}
                                    ref={this.ref}
                                    autoCursor={true}
                                    value={curValue}
                                    options={{ ...options, readOnly: !editable, lineWrapping: canBeWrapped }}
                                    onBeforeChange={(editor, data, value) => {
                                        this.setState({ curValue: value });
                                        onUpdate(value, name);
                                    }}
                                />
                            </div>
                        </>
                    }
                    {selectedFormat === 'json' &&
                        <>
                            <div className={`transform-row-popup-column__one-col-editor ${!isValid ? 'invalid' : ''}`}>
                                <TextFieldWithJsonWrapper
                                    key={inputKey}
                                    value={curValue}
                                    readOnly={!editable}
                                    tabSize={transformsSettings.tabSize}
                                    indentUnit={transformsSettings.indentUnit}
                                    validationMessage={editable ? undefined : 'JSON is not valid'}
                                    noAutoFocus={true}
                                    noBorder={true}
                                    skipValidation={true}
                                    mode={JS_CM_TRANSFORM_MODE}
                                    onChange={(val) => {
                                        this.setState({ curValue: val });
                                        onUpdate(val, name);
                                    }}
                                    onUndo={this.undo} />
                                {!isValid && <i title={jsonValidationMsg} className="icon transform-row-popup-column__err-icon icon-alan-info-sign" />}
                            </div>
                        </>
                    }
                    {(selectedFormat === 'markdown' || selectedFormat === 'response') &&
                        <>
                            <div className={`transform-row-popup-column__two-cols-preview ${viewer}`}>
                                {viewer !== 'preview' && <div className="transform-row-popup-column__two-cols-editor">
                                    <ReactCodeMirror
                                        key={inputKey}
                                        ref={this.ref}
                                        autoCursor={true}
                                        value={curValue}
                                        options={{ ...options, readOnly: !editable, lineWrapping: canBeWrapped }}
                                        onBeforeChange={(editor, data, value) => {
                                            this.setState({ curValue: value });
                                            onUpdate(value, name);
                                        }}
                                    />
                                </div>}

                                {viewer !== 'editor' && <div className="transform-row-popup-column__markdown-preview with-markdown studio-markdown">
                                    <TransformMarkdownViewer value={getMarkdownWithTransformFixes(curValue)} />
                                </div>}
                            </div>
                        </>

                    }

                    {selectedFormat === 'html' &&
                        <>
                            <div className={`transform-row-popup-column__two-cols-preview ${viewer}`}>
                                {viewer !== 'preview' && <div className="transform-row-popup-column__two-cols-editor  ">
                                    <ReactCodeMirror
                                        key={inputKey}
                                        ref={this.ref}
                                        autoCursor={true}
                                        value={curValue}
                                        options={{ ...options, readOnly: !editable, mode: HTML_CM_TRANSFORM_MODE, }}
                                        onBeforeChange={(editor, data, value) => {
                                            this.setState({ curValue: value });
                                            onUpdate(value, name);
                                        }}
                                    />
                                </div>}
                                {viewer !== 'editor' && <div className="transform-row-popup-column__markdown-preview ">
                                    <TransformHtmlViewer value={curValue} />
                                </div>}
                            </div>
                        </>
                    }

                    {selectedFormat === 'javascript' &&
                        <>
                            <div className="transform-row-popup-column__one-col-editor  ">
                                <ReactCodeMirror
                                    key={inputKey}
                                    ref={this.ref}
                                    autoCursor={true}
                                    value={curValue}
                                    options={{ ...options, readOnly: !editable, mode: JS_CM_TRANSFORM_MODE, matchBrackets: true, }}
                                    onBeforeChange={(editor, data, value) => {
                                        this.setState({ curValue: value });
                                        onUpdate(value, name);
                                    }}
                                />
                            </div>
                        </>
                    }
                    {isLoading && <Loader size={LoaderSize.Small} theme={LoaderTheme.Dark} center={true} position="absolute" />}
                </div>
            </div>
        );
    }
}

function shouldShowEditorModeSelector(format: TransformFormatType): boolean {
    const keys = Object.keys(transformFormatsEditorModes[format]);
    return keys.length === 3 && keys.includes('editor') && keys.includes('preview') && keys.includes('preview');
}

export default observer(TransformRowPopupColumn);